import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { useCultivation } from '../contexts/CultivationContext';

const ArtifactRefiningPage = () => {
  const navigate = useNavigate();
  const { user, meritPoints, updateMeritPoints } = useUser();
  const { cultivation } = useCultivation();
  
  const [selectedArtifact, setSelectedArtifact] = useState(null);
  const [activeTab, setActiveTab] = useState('artifacts');
  const [craftingMessage, setCraftingMessage] = useState(null);
  
  // 如果用户未登录，重定向到登录页面
  if (!user) {
    navigate('/login');
    return null;
  }
  
  // 法器数据
  const artifacts = [
    {
      id: 1,
      name: '数字化加湿器葫芦',
      description: '结合传统葫芦造型与现代加湿器功能，内置智能湿度感应系统，自动调节湿度平衡，有助于修炼时保持最佳体感环境。',
      level: 3,
      cost: 300,
      materials: ['灵纹电路板', '灵气传感器', '云纹玉石', '高纯水晶'],
      craftTime: '3小时',
      effects: ['室内湿度自动调节', '释放微量灵气', '改善修炼环境'],
      image: 'humidifier-gourd.jpg',
      type: 'practical'
    },
    {
      id: 2,
      name: '灵能充电宝',
      description: '采用特殊材料打造的充电设备，能够将修炼者多余的灵力转化为电能，为电子设备充电，同时具备净化电子设备内部杂质的功能。',
      level: 2,
      cost: 200,
      materials: ['灵能转换核心', '紫铜导线', '能量水晶', '灵纹电路板'],
      craftTime: '2小时',
      effects: ['灵力转化为电能', '电子设备净化', '提高设备使用寿命'],
      image: 'spirit-powerbank.jpg',
      type: 'practical'
    },
    {
      id: 3,
      name: '冥想辅助头环',
      description: '戴在头部的科技法器，能够监测脑电波并发出与修炼状态相匹配的微弱电磁波，帮助佩戴者更容易进入冥想状态。',
      level: 4,
      cost: 450,
      materials: ['脑波感应元件', '灵能调频器', '银丝导体', '静心宝石'],
      craftTime: '5小时',
      effects: ['脑波调节', '冥想深度提升', '精神力恢复速度+20%'],
      image: 'meditation-headband.jpg',
      type: 'cultivation'
    },
    {
      id: 4,
      name: '数据追踪符箓',
      description: '融合传统符箓与现代编程技术的产物，贴在物品上后能够记录并追踪其位置信息，防止重要物品丢失。',
      level: 1,
      cost: 100,
      materials: ['灵纹墨水', '编程符纸', '定位水晶'],
      craftTime: '1小时',
      effects: ['物品位置追踪', '丢失提醒', '数据记录'],
      image: 'tracking-talisman.jpg',
      type: 'practical'
    },
    {
      id: 5,
      name: '聚灵阵法盘',
      description: '小型便携式聚灵阵法，放置在修炼区域可增加周围灵气浓度，提高修炼效率。内置数字显示屏可实时监测灵气浓度变化。',
      level: 5,
      cost: 600,
      materials: ['聚灵石', '灵纹电路', '五行金属', '阵法核心', '显示模块'],
      craftTime: '8小时',
      effects: ['区域灵气浓度+30%', '修炼效率+15%', '灵气浓度监测'],
      image: 'spirit-gathering-array.jpg',
      type: 'cultivation'
    },
    {
      id: 6,
      name: '记忆强化耳机',
      description: '佩戴在耳朵上的法器，播放特定频率的声波刺激大脑，提高记忆力和学习效率，特别适合学习新技能和记忆经文。',
      level: 3,
      cost: 350,
      materials: ['声波调制器', '记忆增幅石', '灵纹电路', '舒适耳垫'],
      craftTime: '4小时',
      effects: ['记忆力提升+20%', '学习速度+15%', '大脑疲劳缓解'],
      image: 'memory-enhancing-earphones.jpg',
      type: 'cultivation'
    }
  ];
  
  // 获取技能等级
  const getSkillLevel = (skillId) => {
    const exp = cultivation.skills[skillId] || 0;
    if (exp < 100) return 1;
    if (exp < 500) return 2;
    if (exp < 1500) return 3;
    if (exp < 5000) return 4;
    if (exp < 15000) return 5;
    return 6;
  };
  
  // 检查是否可以炼制某个法器
  const canCraftArtifact = (artifactLevel) => {
    const craftingSkillLevel = getSkillLevel('artifact_crafting');
    return craftingSkillLevel >= artifactLevel;
  };
  
  // 炼制法器
  const craftArtifact = (artifact) => {
    if (!canCraftArtifact(artifact.level)) {
      setCraftingMessage({
        type: 'error',
        message: `炼制失败：你的法器锻造技能等级(${getSkillLevel('artifact_crafting')})不足，需要达到${artifact.level}级。`
      });
      return;
    }
    
    if (meritPoints < artifact.cost) {
      setCraftingMessage({
        type: 'error',
        message: `炼制失败：功德点不足，需要${artifact.cost}点功德，当前余额${meritPoints}点。`
      });
      return;
    }
    
    // 模拟炼制成功，扣除功德点
    updateMeritPoints(-artifact.cost);
    
    setCraftingMessage({
      type: 'success',
      message: `炼制成功：已成功炼制【${artifact.name}】，消耗${artifact.cost}点功德。物品已添加到你的背包中。`
    });
  };
  
  // 材料类别
  const materials = [
    { id: 1, name: '灵纹电路板', description: '蚀刻有特殊灵纹的电路板，能够传导灵力与电力。', cost: 50, level: 2 },
    { id: 2, name: '灵气传感器', description: '能感知周围灵气浓度的精密传感器。', cost: 40, level: 2 },
    { id: 3, name: '云纹玉石', description: '具有云纹的特殊玉石，有助于水分子的精细控制。', cost: 80, level: 3 },
    { id: 4, name: '高纯水晶', description: '纯度极高的水晶，具有良好的能量存储和传导特性。', cost: 60, level: 2 },
    { id: 5, name: '灵能转换核心', description: '能够将灵力转换为电能的核心组件。', cost: 90, level: 3 },
    { id: 6, name: '紫铜导线', description: '使用特殊处理的紫铜制成，导电性能优良。', cost: 30, level: 1 },
    { id: 7, name: '能量水晶', description: '能够存储大量能量的特殊水晶。', cost: 70, level: 2 },
    { id: 8, name: '脑波感应元件', description: '能够感知和分析脑电波的精密元件。', cost: 120, level: 4 }
  ];
  
  return (
    <div className="space-y-8">
      {/* 炼器系统头部 */}
      <section className="cyber-panel p-6 rounded-lg bg-gradient-to-r from-cyber-dark to-black">
        <div className="flex flex-col md:flex-row justify-between items-center">
          <div>
            <h1 className="text-3xl font-xianxia text-cyber-primary mb-2 cyber-title">赛博炼器系统</h1>
            <p className="text-cyber-light">
              传统炼器与现代科技的融合，打造实用的赛博法器
            </p>
          </div>
          
          <div className="mt-4 md:mt-0 text-right">
            <div className="text-cyber-accent mb-1">
              <i className="fas fa-hammer mr-2"></i>
              法器锻造等级: <span className="text-xl">{getSkillLevel('artifact_crafting')}</span>
            </div>
            <div className="text-cyber-light">
              功德点: <span className="text-cyber-secondary">{meritPoints}</span>
            </div>
          </div>
        </div>
      </section>
      
      {/* 炼制消息 */}
      {craftingMessage && (
        <div className={`cyber-panel p-4 rounded-lg ${
          craftingMessage.type === 'success' ? 'border-green-500' : 'border-red-500'
        }`}>
          <div className="flex items-start">
            <div className={`mt-0.5 mr-3 text-xl ${
              craftingMessage.type === 'success' ? 'text-green-500' : 'text-red-500'
            }`}>
              <i className={`fas ${craftingMessage.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`}></i>
            </div>
            <div>
              <p className="text-cyber-light">{craftingMessage.message}</p>
              <button 
                className="text-cyber-primary text-sm mt-2 hover:text-cyber-accent"
                onClick={() => setCraftingMessage(null)}
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* 标签导航 */}
      <div className="border-b border-cyber-primary">
        <nav className="flex flex-wrap -mb-px">
          <button
            className={`mr-8 py-2 border-b-2 font-medium text-sm ${
              activeTab === 'artifacts'
                ? 'border-cyber-primary text-cyber-primary'
                : 'border-transparent text-cyber-light hover:text-cyber-secondary hover:border-cyber-secondary'
            }`}
            onClick={() => setActiveTab('artifacts')}
          >
            法器图鉴
          </button>
          <button
            className={`mr-8 py-2 border-b-2 font-medium text-sm ${
              activeTab === 'materials'
                ? 'border-cyber-primary text-cyber-primary'
                : 'border-transparent text-cyber-light hover:text-cyber-secondary hover:border-cyber-secondary'
            }`}
            onClick={() => setActiveTab('materials')}
          >
            材料库
          </button>
          <button
            className={`mr-8 py-2 border-b-2 font-medium text-sm ${
              activeTab === 'crafting'
                ? 'border-cyber-primary text-cyber-primary'
                : 'border-transparent text-cyber-light hover:text-cyber-secondary hover:border-cyber-secondary'
            }`}
            onClick={() => setActiveTab('crafting')}
          >
            炼器技能
          </button>
          <button
            className={`mr-8 py-2 border-b-2 font-medium text-sm ${
              activeTab === 'ar-view'
                ? 'border-cyber-primary text-cyber-primary'
                : 'border-transparent text-cyber-light hover:text-cyber-secondary hover:border-cyber-secondary'
            }`}
            onClick={() => setActiveTab('ar-view')}
          >
            AR展示
          </button>
        </nav>
      </div>
      
      {/* 标签内容 */}
      <div className="tab-content">
        {/* 法器图鉴 */}
        {activeTab === 'artifacts' && (
          <div className="space-y-6">
            {selectedArtifact ? (
              <div className="cyber-panel p-6 rounded-lg">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-cyber text-cyber-primary">
                    {selectedArtifact.name}
                  </h2>
                  <button
                    className="cyber-button"
                    onClick={() => setSelectedArtifact(null)}
                  >
                    返回列表
                  </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <div className="cyber-panel bg-cyber-dark p-2 rounded-lg overflow-hidden h-64 flex items-center justify-center mb-4">
                      <img 
                        src={`/assets/${selectedArtifact.image}`} 
                        alt={selectedArtifact.name}
                        className="max-h-full max-w-full object-contain"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://via.placeholder.com/400x300?text=法器图片';
                        }}
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="cyber-panel p-3 rounded">
                        <div className="text-cyber-secondary text-sm mb-1">等级要求</div>
                        <div className="text-xl text-cyber-primary">
                          {selectedArtifact.level}
                          {!canCraftArtifact(selectedArtifact.level) && (
                            <span className="text-red-500 text-xs ml-2">(技能不足)</span>
                          )}
                        </div>
                      </div>
                      <div className="cyber-panel p-3 rounded">
                        <div className="text-cyber-secondary text-sm mb-1">功德点</div>
                        <div className="text-xl text-cyber-primary">
                          {selectedArtifact.cost}
                          {meritPoints < selectedArtifact.cost && (
                            <span className="text-red-500 text-xs ml-2">(点数不足)</span>
                          )}
                        </div>
                      </div>
                      <div className="cyber-panel p-3 rounded">
                        <div className="text-cyber-secondary text-sm mb-1">类型</div>
                        <div className="text-lg text-cyber-primary">
                          {selectedArtifact.type === 'practical' ? '实用型' : '修炼型'}
                        </div>
                      </div>
                      <div className="cyber-panel p-3 rounded">
                        <div className="text-cyber-secondary text-sm mb-1">炼制时间</div>
                        <div className="text-lg text-cyber-primary">{selectedArtifact.craftTime}</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg text-cyber-secondary mb-2">描述</h3>
                      <p className="text-cyber-light">{selectedArtifact.description}</p>
                    </div>
                    
                    <div>
                      <h3 className="text-lg text-cyber-secondary mb-2">材料需求</h3>
                      <ul className="cyber-panel p-3 rounded space-y-2">
                        {selectedArtifact.materials.map((material, index) => (
                          <li key={index} className="flex items-center">
                            <i className="fas fa-circle text-xs text-cyber-accent mr-2"></i>
                            <span className="text-cyber-light">{material}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div>
                      <h3 className="text-lg text-cyber-secondary mb-2">效果</h3>
                      <ul className="cyber-panel p-3 rounded space-y-2">
                        {selectedArtifact.effects.map((effect, index) => (
                          <li key={index} className="flex items-center">
                            <i className="fas fa-check text-xs text-cyber-primary mr-2"></i>
                            <span className="text-cyber-light">{effect}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <button
                      className={`cyber-button w-full ${
                        !canCraftArtifact(selectedArtifact.level) || meritPoints < selectedArtifact.cost
                          ? 'opacity-50 cursor-not-allowed'
                          : ''
                      }`}
                      onClick={() => craftArtifact(selectedArtifact)}
                      disabled={!canCraftArtifact(selectedArtifact.level) || meritPoints < selectedArtifact.cost}
                    >
                      炼制法器
                    </button>
                    
                    <Link
                      to="/ar-view"
                      className="cyber-button w-full text-center"
                    >
                      AR预览
                    </Link>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="cyber-panel p-6 rounded-lg">
                  <h2 className="text-2xl font-cyber text-cyber-primary mb-6">
                    赛博法器图鉴
                  </h2>
                  <p className="text-cyber-light mb-8">
                    天道宗赛博法器图鉴收录了传统法器与现代科技结合的产物，每件法器都具有独特功效，辅助弟子修炼或日常生活。
                    法器锻造需要一定的技能等级和材料，炼制成功后可以永久使用。
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {artifacts.map((artifact) => (
                      <div key={artifact.id} className="cultivation-item flex flex-col">
                        <div className="flex justify-between items-start mb-3">
                          <h3 className="text-lg text-cyber-secondary">
                            {artifact.name}
                            <span className="ml-2 text-xs border border-cyber-primary px-2 py-0.5 rounded-full">
                              Lv.{artifact.level}
                            </span>
                          </h3>
                          <div className="text-cyber-accent">
                            <i className="fas fa-coins mr-1"></i> {artifact.cost}
                          </div>
                        </div>
                        <p className="text-cyber-light text-sm mb-3 flex-grow">
                          {artifact.description.length > 120 
                            ? artifact.description.substring(0, 120) + '...' 
                            : artifact.description}
                        </p>
                        <div className="flex justify-between items-center mt-2">
                          <div className="text-xs text-cyber-light">
                            类型: {artifact.type === 'practical' ? '实用型' : '修炼型'}
                          </div>
                          <button
                            className="cyber-button text-sm"
                            onClick={() => setSelectedArtifact(artifact)}
                          >
                            查看详情
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
        
        {/* 材料库 */}
        {activeTab === 'materials' && (
          <div className="cyber-panel p-6 rounded-lg">
            <h2 className="text-2xl font-cyber text-cyber-primary mb-6">
              炼器材料库
            </h2>
            <p className="text-cyber-light mb-8">
              炼制赛博法器需要各种特殊材料，这些材料结合了传统修真物品与现代科技元件的特性。
              可以通过功德点兑换或在线下道场特定活动中获取。
            </p>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-cyber-primary">
                    <th className="text-left py-3 text-cyber-primary">材料名称</th>
                    <th className="text-left py-3 text-cyber-primary">描述</th>
                    <th className="text-left py-3 text-cyber-primary">等级</th>
                    <th className="text-right py-3 text-cyber-primary">功德点</th>
                    <th className="text-right py-3 text-cyber-primary">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {materials.map((material) => (
                    <tr key={material.id} className="border-b border-gray-700">
                      <td className="py-4 text-cyber-secondary">{material.name}</td>
                      <td className="py-4 text-cyber-light text-sm max-w-xs">{material.description}</td>
                      <td className="py-4 text-cyber-light">{material.level}</td>
                      <td className="py-4 text-cyber-accent text-right">{material.cost}</td>
                      <td className="py-4 text-right">
                        <button className="cyber-button text-xs">
                          购买
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        
        {/* 炼器技能 */}
        {activeTab === 'crafting' && (
          <div className="cyber-panel p-6 rounded-lg">
            <h2 className="text-2xl font-cyber text-cyber-primary mb-6">
              法器锻造技能
            </h2>
            
            <div className="mb-8">
              <div className="flex justify-between mb-1">
                <span className="text-cyber-light">当前等级: {getSkillLevel('artifact_crafting')}</span>
                <span className="text-cyber-light">下一等级: {getSkillLevel('artifact_crafting') + 1}</span>
              </div>
              <div className="cultivation-progress">
                <div 
                  className="cultivation-progress-bar" 
                  style={{ width: '45%' }}
                ></div>
              </div>
              <div className="text-right mt-1">
                <span className="text-cyber-light text-sm">
                  经验: {cultivation.skills['artifact_crafting'] || 0} / 
                  {getSkillLevel('artifact_crafting') === 1 ? 100 : 
                   getSkillLevel('artifact_crafting') === 2 ? 500 : 
                   getSkillLevel('artifact_crafting') === 3 ? 1500 : 
                   getSkillLevel('artifact_crafting') === 4 ? 5000 : 15000}
                </span>
              </div>
            </div>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-lg text-cyber-secondary mb-3">技能说明</h3>
                <p className="text-cyber-light">
                  法器锻造是将传统炼器技术与现代科技结合的技能，通过提升该技能等级，可以炼制更高级的赛博法器。
                  练习该技能可以提升敏捷和科技属性，对精细操作和理解现代科技有所帮助。
                </p>
              </div>
              
              <div>
                <h3 className="text-lg text-cyber-secondary mb-3">等级权限</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="cyber-panel p-3 rounded">
                    <div className="text-lg text-cyber-primary mb-1">1-2级</div>
                    <p className="text-cyber-light text-sm">
                      可炼制基础级赛博法器，如数据追踪符箓、简易灵能装置等。
                    </p>
                  </div>
                  <div className="cyber-panel p-3 rounded">
                    <div className="text-lg text-cyber-primary mb-1">3-4级</div>
                    <p className="text-cyber-light text-sm">
                      可炼制中级赛博法器，如数字化加湿器葫芦、冥想辅助头环等。
                    </p>
                  </div>
                  <div className="cyber-panel p-3 rounded">
                    <div className="text-lg text-cyber-primary mb-1">5级以上</div>
                    <p className="text-cyber-light text-sm">
                      可炼制高级赛博法器，如聚灵阵法盘、虚拟修炼空间等。
                    </p>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg text-cyber-secondary mb-3">提升方法</h3>
                <ul className="cyber-panel p-4 rounded space-y-3">
                  <li className="flex items-start">
                    <i className="fas fa-angle-right text-cyber-primary mt-1 mr-2"></i>
                    <div>
                      <span className="text-cyber-light">炼制法器：</span>
                      <span className="text-cyber-light text-sm">成功炼制法器可获得大量经验，等级越高的法器提供的经验越多。</span>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <i className="fas fa-angle-right text-cyber-primary mt-1 mr-2"></i>
                    <div>
                      <span className="text-cyber-light">学习图纸：</span>
                      <span className="text-cyber-light text-sm">研究法器图纸可获得少量经验，适合初学者。</span>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <i className="fas fa-angle-right text-cyber-primary mt-1 mr-2"></i>
                    <div>
                      <span className="text-cyber-light">线下道场：</span>
                      <span className="text-cyber-light text-sm">参加线下道场的炼器课程，可获得大量经验和实践机会。</span>
                    </div>
                  </li>
                </ul>
              </div>
              
              <div className="text-center">
                <Link to="/cultivation" className="cyber-button">
                  查看所有技能
                </Link>
              </div>
            </div>
          </div>
        )}
        
        {/* AR展示 */}
        {activeTab === 'ar-view' && (
          <div className="cyber-panel p-6 rounded-lg">
            <h2 className="text-2xl font-cyber text-cyber-primary mb-6">
              AR法器展示
            </h2>
            
            <div className="text-center py-8">
              <p className="text-cyber-light mb-6">
                使用AR技术查看和体验各种赛博法器的3D模型和功能演示。
                需要使用AR应用或访问专门的AR展示页面。
              </p>
              
              <Link to="/ar-view" className="cyber-button">
                进入AR展示页面
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArtifactRefiningPage;
